import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type EyebrowElement = 'div' | 'span' | 'label' | 'legend' | 'h2' | 'h3';

export interface EyebrowProps extends HTMLAttributes<HTMLElement> {
  /** Rendered element — 'label' for form fields, 'h2'/'h3' for rail/section headings. */
  as?: EyebrowElement;
  /** t3 (default) on card/canvas surfaces; t2 on the darker rail/bg surfaces (AA). */
  tone?: 't3' | 't2';
  /** Forwarded when as="label". */
  htmlFor?: string;
}

/**
 * The mono section label — uppercase Geist Mono at the eyebrow size, the
 * system's most-repeated recipe (~40 hand-rolled copies before this existed).
 * Spacing (mb-1 etc.) and weight tweaks stay at the call site via className.
 */
export const Eyebrow = forwardRef<HTMLElement, EyebrowProps>(function Eyebrow(
  { as = 'div', tone = 't3', className, ...rest },
  ref,
) {
  const Comp = as;
  return (
    <Comp
      // Polymorphic ref: the union's intersected ref types are unrepresentable.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      ref={ref as any}
      className={cn(
        'block font-mono text-eyebrow uppercase',
        tone === 't3' ? 'text-t3' : 'text-t2',
        className,
      )}
      {...rest}
    />
  );
});
